import { useEffect } from "react";

// Body-scroll lock, REF-COUNTED at module scope so it is order-independent. The FIRST overlay to open locks
// the page and the LAST to close restores it. A per-layer capture/restore would latch the lock if two
// overlays unmounted in the same commit (React runs passive cleanups in forward mount order, so an earlier
// layer would restore "" before a later layer restored the "hidden" value it had captured). The count
// survives StrictMode's dev setup/cleanup/setup double-invoke.
//
// It locks the ROOT element, not <body>, and that distinction is the whole fix. The viewport takes its
// overflow from <body> when <html> is `visible`, so `body { overflow: hidden }` makes the VIEWPORT
// unscrollable and the browser clamps the offset to 0 — every modal threw the page behind it to the top and
// left it there on close (measured in-app 2026-08-23: scrollY 1200 → 0 the instant the lock landed, with
// `scrollHeight` unchanged, which is what ruled out a layout collapse). Hiding the root's overflow instead
// stops the wheel without touching the offset, so nothing has to be saved and restored — and nothing can go
// stale if the effect is re-run.
//
// The counter lives HERE rather than inside `useModalA11y` so every overlay in the app — the shared
// primitives AND a hand-rolled portal that cannot adopt the full a11y hook (the messages inbox owns its own
// two-layer Escape) — shares ONE lock: a hand-rolled overlay calling this hook counts against the same total
// as a Modal stacked over it, so opening one while the other is up never double-locks or early-restores.
let scrollLockCount = 0;

/**
 * Stamped on `<html>` while any overlay holds the lock. Hiding the root's overflow freezes a page that scrolls
 * the DOCUMENT; the mobile shells don't — their content is a scroll pane of its own inside a fixed frame, which
 * the root's overflow never reaches, so a touch-scroll over a sheet dragged the page behind it (ART-251). Each
 * such pane freezes itself on this attribute (`overflow: hidden` keeps its scroll offset, so nothing jumps).
 */
export const SCROLL_LOCKED_ATTR = "data-scroll-locked";
let savedRootOverflow = "";
let savedBodyPaddingRight = "";

// THE TOUCH HALF OF THE LOCK. `overflow: hidden` on the root and on the shells' panes is what stops a wheel; a TOUCH
// gesture that runs off a fixed overlay (a drag on the scrim, or on a sheet already at its scroll edge) is the
// browser's to chain, so while locked a non-passive touchmove cancels any vertical gesture that no scroller under the
// finger can absorb. NOT what fixed the "page moves behind the opportunity sheet" report (ART-251): that was the
// mobile shell's pull-to-refresh hearing the sheet's touches through React's portal bubbling — see usePullToRefresh.
// A scroller that CAN move in that direction keeps its native scroll, a sideways gesture is never touched (photo strips, sliders), and a frozen pane (`overflow: hidden`) never counts.
let touchStartX = 0;
let touchStartY = 0;

function canAbsorb(el: Element, dy: number): boolean {
  const oy = getComputedStyle(el).overflowY;
  if (oy !== "auto" && oy !== "scroll") return false;
  if (el.scrollHeight <= el.clientHeight) return false;
  // Finger moving DOWN scrolls the content toward its top.
  return dy > 0 ? el.scrollTop > 0 : el.scrollTop + el.clientHeight < el.scrollHeight - 1;
}

function onLockedTouchStart(e: TouchEvent) {
  const t = e.touches[0];
  if (!t) return;
  touchStartX = t.clientX;
  touchStartY = t.clientY;
}

function onLockedTouchMove(e: TouchEvent) {
  if (e.touches.length !== 1 || !e.cancelable) return; // pinch-zoom stays the browser's
  const t = e.touches[0]!;
  const dx = t.clientX - touchStartX;
  const dy = t.clientY - touchStartY;
  if (Math.abs(dx) > Math.abs(dy)) return;
  for (let el = e.target instanceof Element ? e.target : null; el && el !== document.documentElement; el = el.parentElement) {
    if (canAbsorb(el, dy)) return;
  }
  e.preventDefault();
}

/**
 * Freeze the page behind an overlay while it is open — the wheel/touch scroll of the content BEHIND the
 * scrim, which every dialog needs frozen and which `position: fixed` on the overlay itself does not provide.
 *
 * Ref-counted and idempotent, so stacked overlays lock once and unlock once regardless of mount/unmount
 * ordering. `useModalA11y` folds this in; call it directly only from an overlay that cannot use that hook
 * wholesale (a custom Escape contract, say) but must still stop the page moving behind it.
 *
 * `enabled` lets a component that is SOMETIMES an overlay (the inbox, which is an in-flow tab PAGE on a mobile
 * list) keep the hook call unconditional while not locking in its page mode — pass `false` and it is inert,
 * counting against nothing.
 */
export function useBodyScrollLock(enabled = true) {
  useEffect(() => {
    if (!enabled) return;
    if (scrollLockCount === 0) {
      const root = document.documentElement;
      // The gutter the scrollbar occupied: reserve it while locked, or the page jerks sideways under the
      // scrim on every platform that paints a classic scrollbar (0 where they overlay, e.g. macOS).
      const gutter = window.innerWidth - root.clientWidth;
      savedRootOverflow = root.style.overflow;
      savedBodyPaddingRight = document.body.style.paddingRight;
      root.style.overflow = "hidden";
      root.setAttribute(SCROLL_LOCKED_ATTR, "");
      if (gutter > 0) document.body.style.paddingRight = `${gutter}px`;
      document.addEventListener("touchstart", onLockedTouchStart, { passive: true });
      document.addEventListener("touchmove", onLockedTouchMove, { passive: false });
    }
    scrollLockCount += 1;
    return () => {
      scrollLockCount -= 1;
      if (scrollLockCount === 0) {
        document.documentElement.style.overflow = savedRootOverflow;
        document.documentElement.removeAttribute(SCROLL_LOCKED_ATTR);
        document.body.style.paddingRight = savedBodyPaddingRight;
        document.removeEventListener("touchstart", onLockedTouchStart);
        document.removeEventListener("touchmove", onLockedTouchMove);
      }
    };
  }, [enabled]);
}
